import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronRight, 
  ShoppingCart, 
  Check, 
  Leaf, 
  Droplet, 
  ChevronDown,
  ChevronUp,
  Package,
  Award,
  Clock,
  Gift,
  X,
  ChevronLeft,
  ZoomIn,
  Truck,
  AlertCircle
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { getPriceInfo, isDiscountActive, getDiscountPercentage } from '../config/discountConfig';
import { getProductStock, STOCK_STATUS } from '../config/productConfig';
import RelatedProducts from '../components/RelatedProducts';
import spoonImg from '../assets/images/spoon.jpg';
import tastymaxi_8 from "../assets/Cutlery/tastymaxi_8.jpg";
import tastymaxi_99 from "../assets/Cutlery/tastymaxi_99.jpg";
import tastymaxi_22 from "../assets/Cutlery/tastymaxi_22.jpg";


const EdibleSpoons = () => {
  const [selectedQuantity, setSelectedQuantity] = useState('50');
  const [activeTab, setActiveTab] = useState('description');
  const [selectedImage, setSelectedImage] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isQuantityDropdownOpen, setIsQuantityDropdownOpen] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);
  const [isAddedToCart, setIsAddedToCart] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const productImageRef = useRef(null);
  
  const { addToCartWithAnimation } = useCart();
  
  // Get stock status from config
  const stockInfo = getProductStock('edible-spoons');

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isLightboxOpen) return;
      
      if (e.key === 'Escape') {
        setIsLightboxOpen(false);
      } else if (e.key === 'ArrowRight') {
        setLightboxImageIndex((prev) => (prev + 1) % productImages.length);
      } else if (e.key === 'ArrowLeft') {
        setLightboxImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen]);

  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isLightboxOpen]);

  const openLightbox = (index) => {
    setLightboxImageIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const goToNextImage = () => {
    setLightboxImageIndex((prev) => (prev + 1) % productImages.length);
  };

  const goToPrevImage = () => {
    setLightboxImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  const pricingOptions = [
    { units: 'sample', price: 0, label: 'Sample', description: 'Free sample' },
    { units: 10, price: 2.99, label: '10 spoons', description: '1 pack' },
  ];

  const getSelectedPricing = () => {
    return pricingOptions.find(opt => String(opt.units) === selectedQuantity) || pricingOptions[1];
  };

  const selectedPricing = getSelectedPricing();

  const productImages = [
    { id: 0, src: spoonImg, alt: "Edible Spoons - Main Product View" },
    { id: 1, src: tastymaxi_8, alt: "Edible Spoons - Detail View" },
    { id: 2, src: tastymaxi_99, alt: "Edible Spoons - In Use" },
    { id: 3, src: tastymaxi_22, alt: "Edible Spoons - In Use" }
  ];

  const product = {
    name: "Edible Spoons",
    subtitle: "Sustainable Spoons for Every Occasion",
    sku: "CUTLERY-SPOON-001",
    unitsPerPack: 10
  };

  const keyFeatures = [
    "Perfect for Amuse-bouche, Desserts, yogurt & soups",
    "Crispy texture with pleasant taste",
    "Very Stable in hot/cold foods",
    "ISO 22000, FSSAI & FDA certified",
    "100% Edible & Biodegradable EATlery® by FRENVI made in Germany",
  ];

  const productDetails = {
    dimensions: {
      length: "14 cm",
      width: "3.5 cm",
      weight: "12g per spoon",
      capacity: "Standard size for amuse-bouche and dessert"
    },
ingredients: [
  "Wheat flour, Sorghum flour",
  "Brown sugar, Invert sugar syrup, Refined wheat flour, Rice bran oil",
  "Wheat protein, Soy lecithin",
  "Iodized salt, Vanillin, Potassium carbonate",
  "Added antioxidants, Natural flavors (vanilla)",
],
nutritionalInfo: {
  calories: "48.98 kcal",
  fat: "0.55 g",
  monounsaturatedFat: "0.18 g",
  carbohydrates: "9.74 g",
  sugars: "2.14 g",
  fiber: "1.10 g",
  protein: "1.27 g",
  salt: "0.0113 g"
},
    storage: "Store in a cool, dry place (15-25°C) away from direct sunlight. Keep in original packaging until use. Best consumed within 18 months of production date.",
    certifications: ["ISO 22000", "FSSAI Certified", "FDA Approved", "Vegan Friendly"],
    shelfLife: "18 months from production date"
  };

  const faqs = [
    {
      question: "What are the edible spoons best used for?",
      answer: "Our edible spoons are perfect for ice cream, yogurt, puddings, soups, and any dessert or meal that requires a spoon. They're ideal for events, catering, food trucks, and eco-conscious businesses."
    },
    {
      question: "How long do the spoons stay stable in food?",
      answer: "The spoons maintain their structural integrity for over 60 minutes when used with hot or cold foods. This gives you plenty of time to enjoy your meal without worrying about the spoon getting soggy."
    },
    {
      question: "Are the spoons suitable for hot soups?",
      answer: "Yes! Our edible spoons are heat-resistant and perfect for hot soups and beverages up to 85°C. They won't melt or become too soft during normal use."
    },
    {
      question: "Do the spoons contain any allergens?",
      answer: "The spoons contain wheat and gluten. Please check the full ingredients list if you have specific dietary requirements or allergies."
    }
  ];

  return (
    <div className="edible-cup-page">
      <nav className="breadcrumb-nav">
        <div className="container">
          <ol className="breadcrumb">
            <li><Link to="/">Home</Link></li>
            <ChevronRight size={14} />
            <li><Link to="/cutlery-tableware">Cutlery & Tableware</Link></li>
            <ChevronRight size={14} />
            <li className="current">Edible Spoons</li>
          </ol>
        </div>
      </nav>

      <section className="product-main-section">
        <div className="container">
          <div className="product-layout">
            <div className="product-gallery">
              <div className="main-image-container" onClick={() => openLightbox(selectedImage)}>
                <span className="zoom-hint">
                  <ZoomIn size={20} />
                  Click to zoom
                </span>
                <img 
                  ref={productImageRef}
                  src={productImages[selectedImage].src} 
                  alt={productImages[selectedImage].alt}
                  className="main-product-image clickable"
                />
              </div>
              <div className="thumbnail-gallery">
                {productImages.map((img, index) => (
                  <button
                    key={img.id}
                    className={`thumbnail-btn ${selectedImage === index ? 'active' : ''}`}
                    onClick={() => setSelectedImage(index)}
                    onDoubleClick={() => openLightbox(index)}
                  >
                    <img src={img.src} alt={img.alt} />
                  </button>
                ))}
              </div>
              
              {/* Key Features - Under Image */}
              <div className="key-features">
                <h3>Key Features</h3>
                <ul>
                  {keyFeatures.map((feature, index) => (
                    <li key={index}>
                      <Leaf size={16} className="feature-icon" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="product-info">
              <span className="product-sku">SKU: {product.sku}</span>
              <h1 className="product-title">{product.name}</h1>
              <p className="product-subtitle">{product.subtitle}</p>

              <div className="carton-pricing">
                {selectedPricing.units === 'sample' ? (
                  <div className="price-main">
                    <span className="price free">FREE</span>
                    <span className="price-label">Sample</span>
                  </div>
                ) : (
                  <>
                    {(() => {
                      const priceInfo = getPriceInfo(selectedPricing.price);
                      return (
                        <div className={`price-main ${priceInfo.hasDiscount ? 'has-discount' : ''}`}>
                          {priceInfo.hasDiscount && (
                            <>
                              <span className="discount-badge">-{priceInfo.discountPercentage}</span>
                              <span className="price-original">€{priceInfo.originalPrice.toFixed(2)}</span>
                            </>
                          )}
                          <span className={`price ${priceInfo.hasDiscount ? 'discounted' : ''}`}>
                            €{priceInfo.discountedPrice.toFixed(2)}
                          </span>
                          <span className="price-label">for {selectedPricing.units} spoons</span>
                        </div>
                      );
                    })()}
                  </>
                )}
                <div className={`stock-status ${stockInfo.isSoldOut ? 'sold-out' : 'in-stock'}`}>
                  {stockInfo.isSoldOut ? (
                    <>
                      <AlertCircle size={20} />
                      {stockInfo.statusLabel}
                    </>
                  ) : (
                    <>
                      <Check size={20} />
                      {stockInfo.statusLabel}
                    </>
                  )}
                </div>
                {stockInfo.isInStock && (
                  <div className="delivery-time">
                    <Truck size={16} />
                    <span>Delivery: {stockInfo.deliveryTime}</span>
                  </div>
                )}
              </div>

              <div className="carton-info-box">
                <Package size={20} />
                <div>
                  <strong>Sold by Pack</strong>
                  <span>Each pack contains {product.unitsPerPack} spoons</span>
                </div>
              </div>

              <div className="size-info-box">
                <Leaf size={20} />
                <div>
                  <strong>Eco-Friendly Alternative</strong>
                  <span>Replace plastic and wood spoons with edible ones</span>
                </div>
              </div>

              <div className="purchase-controls-carton">
                <div className="quantity-dropdown-container">
                  <label>Select Quantity:</label>
                  <div className="quantity-dropdown">
                    <button 
                      className="quantity-dropdown-trigger"
                      onClick={() => setIsQuantityDropdownOpen(!isQuantityDropdownOpen)}
                    >
                      <span>
                        {selectedPricing.units === 'sample' ? (
                          <>
                            <Gift size={16} />
                            Sample - FREE
                          </>
                        ) : (
                          <>
                            {(() => {
                              const priceInfo = getPriceInfo(selectedPricing.price);
                              return (
                                <>
                                  {selectedPricing.units} spoons - 
                                  {priceInfo.hasDiscount && (
                                    <span className="dropdown-original-price">€{priceInfo.originalPrice.toFixed(2)}</span>
                                  )}
                                  <span className={priceInfo.hasDiscount ? 'dropdown-discounted-price' : ''}>
                                    €{priceInfo.discountedPrice.toFixed(2)}
                                  </span>
                                </>
                              );
                            })()}
                          </>
                        )}
                      </span>
                      <ChevronDown size={18} className={isQuantityDropdownOpen ? 'rotate' : ''} />
                    </button>
                    
                    {isQuantityDropdownOpen && (
                      <div className="quantity-dropdown-menu">
                        {pricingOptions.map((option) => (
                          <button
                            key={option.units}
                            className={`quantity-option ${String(option.units) === selectedQuantity ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedQuantity(String(option.units));
                              setIsQuantityDropdownOpen(false);
                            }}
                          >
                            <div className="option-main">
                              {option.units === 'sample' ? (
                                <>
                                  <Gift size={16} />
                                  <span className="option-cups">Sample</span>
                                </>
                              ) : (
                                <>
                                  <span className="option-cups">{option.units} spoons</span>
                                  <span className="option-cartons">({option.description})</span>
                                </>
                              )}
                            </div>
                            <div className="option-price">
                              {option.units === 'sample' ? (
                                <span className="free-tag">FREE</span>
                              ) : (
                                <>
                                  {(() => {
                                    const optPriceInfo = getPriceInfo(option.price);
                                    return (
                                      <>
                                        {optPriceInfo.hasDiscount && (
                                          <span className="option-original-price">€{optPriceInfo.originalPrice.toFixed(2)}</span>
                                        )}
                                        <span className={optPriceInfo.hasDiscount ? 'option-discounted-price' : ''}>
                                          €{optPriceInfo.discountedPrice.toFixed(2)}
                                        </span>
                                      </>
                                    );
                                  })()}
                                </>
                              )}
                            </div>
                            {String(option.units) === selectedQuantity && <Check size={16} className="check-icon" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button 
                  className={`add-to-cart-btn ${isAddedToCart ? 'added' : ''} ${isAddingToCart ? 'loading' : ''} ${stockInfo.isSoldOut ? 'disabled sold-out-btn' : ''}`}
                  disabled={stockInfo.isSoldOut || isAddingToCart}
                  onClick={() => {
                    if (stockInfo.isSoldOut || isAddingToCart) return;
                    
                    if (selectedPricing.units === 'sample') {
                      window.location.href = '/contact';
                      return;
                    }
                    
                    setIsAddingToCart(true);
                    
                    const cartItem = {
                      id: 'edible-spoons',
                      name: 'Edible Spoons',
                      variant: `${selectedPricing.units} spoons`,
                      price: selectedPricing.price,
                      units: selectedPricing.units,
                      quantity: 1,
                      image: productImages[0].src
                    };
                    
                    setTimeout(() => {
                      setIsAddingToCart(false);
                      addToCartWithAnimation(cartItem, productImageRef.current);
                      setIsAddedToCart(true);
                      setTimeout(() => setIsAddedToCart(false), 2000);
                    }, 300);
                  }}
                >
                  {stockInfo.isSoldOut ? (
                    <>
                      <AlertCircle size={20} />
                      Sold Out
                    </>
                  ) : isAddingToCart ? (
                    <span className="btn-loading-text">Adding...</span>
                  ) : isAddedToCart ? (
                    <>
                      <Check size={20} />
                      Added to Cart!
                    </>
                  ) : (
                    <>
                      <ShoppingCart size={20} />
                      {selectedPricing.units === 'sample' ? (
                        'Request Free Sample'
                      ) : (
                        <>
                          {(() => {
                            const btnPriceInfo = getPriceInfo(selectedPricing.price);
                            return (
                              <>
                                Add to Cart - 
                                {btnPriceInfo.hasDiscount && (
                                  <span className="btn-original-price">€{btnPriceInfo.originalPrice.toFixed(2)}</span>
                                )}
                                <span className={btnPriceInfo.hasDiscount ? 'btn-discounted-price' : ''}>
                                  €{btnPriceInfo.discountedPrice.toFixed(2)}
                                </span>
                              </>
                            );
                          })()}
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="product-tabs-section">
        <div className="container">
          <div className="tabs-navigation">
            <button 
              className={`tab-btn ${activeTab === 'description' ? 'active' : ''}`}
              onClick={() => setActiveTab('description')}
            >
              Description
            </button>
            <button 
              className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Product Details
            </button>
            <button 
              className={`tab-btn ${activeTab === 'faq' ? 'active' : ''}`}
              onClick={() => setActiveTab('faq')}
            >
              FAQ
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'description' && (
              <div className="tab-panel description-panel">
                <h2>Edible Spoons - The Sustainable Alternative</h2>
                <p>
                  Edible spoons are a smart alternative to disposable plastic cutlery. Made from natural ingredients and certified to ISO 22000, FSSAI, and FDA standards, they combine food safety with sustainability.
                </p>
                <p>
                  Available in sweet and savory flavors, including vanilla, chocolate, and salty, they pair perfectly with ice cream, yogurt, soups, curries, porridge, desserts, and takeaway meals.
                </p>
                <p>
                  Their sturdy 3D shape stays firm while you eat, and their long shelf life makes them practical for restaurants, catering, events, and retail.
                </p>
                <p>
                  Simply use them like any regular spoon, then enjoy them as part of the meal. By switching to edible spoons, you reduce plastic waste while offering customers something distinctive, practical, and memorable.
                </p>
                
                <div className="description-features">
                  <div className="desc-feature">
                    <Package size={32} />
                    <h4>Perfect Size</h4>
                    <p>14cm length fits comfortably in hand. Ideal for all desserts and soups.</p>
                  </div>
                  <div className="desc-feature">
                    <Droplet size={32} />
                    <h4>Long-Lasting Stability</h4>
                    <p>Maintains structure for many minutes. No soggy spoons here!</p>
                  </div>
                  <div className="desc-feature">
                    <Leaf size={32} />
                    <h4>100% Natural</h4>
                    <p>Made from natural ingredients. Fully biodegradable and eco-friendly.</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'details' && (
              <div className="tab-panel details-panel">
                <div className="details-grid">
                  <div className="detail-section">
                    <h3><Package size={20} /> Dimensions</h3>
                    <table className="details-table">
                      <tbody>
                        <tr><td>Length</td><td>{productDetails.dimensions.length}</td></tr>
                        <tr><td>Width</td><td>{productDetails.dimensions.width}</td></tr>
                        <tr><td>Weight</td><td>{productDetails.dimensions.weight}</td></tr>
                        <tr><td>Size</td><td>{productDetails.dimensions.capacity}</td></tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="detail-section">
                    <h3><Leaf size={20} /> Ingredients</h3>
                    <ul className="ingredients-list">
                      {productDetails.ingredients.map((ingredient, index) => (
                        <li key={index}>{ingredient}</li>
                      ))}
                    </ul>
                    <p className="allergen-warning">
                      <strong>Allergen Information:</strong> Contains wheat and gluten.
                    </p>
                  </div>

                  <div className="detail-section">
                    <h3><Award size={20} /> Nutritional Information (per spoon)</h3>
                    <table className="details-table">
                      <tbody>
                        <tr><td>Energy</td><td>{productDetails.nutritionalInfo.calories}</td></tr>
                        <tr><td>Protein</td><td>{productDetails.nutritionalInfo.protein}</td></tr>
                        <tr><td>Carbohydrates</td><td>{productDetails.nutritionalInfo.carbohydrates}</td></tr>
                        <tr><td>Fat</td><td>{productDetails.nutritionalInfo.fat}</td></tr>
                        <tr><td>Fiber</td><td>{productDetails.nutritionalInfo.fiber}</td></tr>
                        <tr><td>Salt</td><td>{productDetails.nutritionalInfo.salt}</td></tr>
                        <tr><td>Sugars</td><td>{productDetails.nutritionalInfo.sugars}</td></tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="detail-section">
                    <h3><Clock size={20} /> Storage & Shelf Life</h3>
                    <p>{productDetails.storage}</p>
                    <p><strong>Shelf Life:</strong> {productDetails.shelfLife}</p>
                  </div>

                  <div className="detail-section full-width">
                    <h3><Award size={20} /> Certifications</h3>
                    <div className="certifications-list">
                      {productDetails.certifications.map((cert, index) => (
                        <span key={index} className="cert-badge">{cert}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'faq' && (
              <div className="tab-panel faq-panel">
                <h2>Frequently Asked Questions - Edible Spoons</h2>
                <div className="faq-list">
                  {faqs.map((faq, index) => (
                    <div 
                      key={index} 
                      className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
                    >
                      <button 
                        className="faq-question"
                        onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      >
                        <span>{faq.question}</span>
                        {expandedFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>
                      {expandedFaq === index && (
                        <div className="faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related Products Section */}
      <RelatedProducts currentProductId="spoons" category="cutlery-tableware" />

      {isLightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <X size={28} />
            </button>
            
            <button className="lightbox-nav lightbox-prev" onClick={goToPrevImage}>
              <ChevronLeft size={36} />
            </button>
            
            <div className="lightbox-image-container">
              <img 
                src={productImages[lightboxImageIndex].src} 
                alt={productImages[lightboxImageIndex].alt}
                className="lightbox-image"
              />
            </div>
            
            <button className="lightbox-nav lightbox-next" onClick={goToNextImage}>
              <ChevronRight size={36} />
            </button>
            
            <div className="lightbox-thumbnails">
              {productImages.map((img, index) => (
                <button
                  key={img.id}
                  className={`lightbox-thumb ${lightboxImageIndex === index ? 'active' : ''}`}
                  onClick={() => setLightboxImageIndex(index)}
                >
                  <img src={img.src} alt={img.alt} />
                </button>
              ))}
            </div>
            
            <div className="lightbox-counter">
              {lightboxImageIndex + 1} / {productImages.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EdibleSpoons;
